import { AppContext } from "components/App/AppContext";
import { ErrorSummary } from "components/ErrorSummary";
import { PageLink } from "components/App/utils";
import { Icon } from "components/Icon";
import { JsonForm } from "components/JsonForm";
import { IconInputWidget } from "components/JsonForm/theme/widgets/IconInputWidget";
import { SubmitButton } from "components/Button/SubmitButton";
import { Container } from "components/Layout/Container";
import { verifyAuthCode, createAccount, userNewAuthCode, usePasswordRequirements } from "components/utils";
import { isEmpty } from "lodash";
import { useState, useContext, useEffect, useCallback } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { usePageTopAndBottomRows } from "components/utils/page";
import { PageComponentsList } from ".";
import { getCustomerNumber, saveUser } from "components/utils/user";
import { AnalyticsEventType, sendAnalyticsEvent } from "components/utils/analytics";
import { Button } from "../Button";
import { EmailButton } from "../Button/EmailButton";
import { Alert } from "react-bootstrap";

import { Heading } from "../Heading";
import { HeadingLevel } from "../../types/HeadingLevel";

export function CreateAccountPage() {
    const location = useLocation<{ referrer: string }>();
    const [validated, setValidated] = useState(false);
    const [accountFormData, setAccountFormData] = useState<any>();
    const [customerId, setCustomerId] = useState<string>();
    const [pageNumber, setPageNumber] = useState<string | null>(null);
    const [showNewAuthButton, setShowNewAuthButton] = useState<boolean>(false);
    const { topRows, bottomRows } = usePageTopAndBottomRows();

    useEffect(() => {
        const referrer = location?.state?.referrer;
        if (referrer?.startsWith(PageLink.CustomerAuthCode)) {
            const pathParts = referrer.split("?")[0].split("/");
            if (!isEmpty(pathParts[2])) {
                setValidated(true);
                setCustomerId(pathParts[2]);
            }
            const urlParams = new URLSearchParams(`?${referrer.split("?")[1]}`);
            setPageNumber(urlParams.get("pagenumber"));
            setShowNewAuthButton(true);
        }
    }, [location]);

    useEffect(() => {
        const interval = setInterval(() => {
            setShowNewAuthButton(true);
        }, THIRTY_MINUTES);

        return () => clearInterval(interval);
    }, []);

    const onAccountSubmit = useCallback((formData: any, pageNumber: string | null) => {
        setAccountFormData(formData);
        setPageNumber(pageNumber);
    }, []);

    return (
        <>
            <PageComponentsList components={topRows} />
            <Container className="p-0">
                <div className="d-flex justify-content-center w-100">
                    <div className="bg-white d-flex flex-column gap-4 p-4 w-100 rounded-5p5 shadow-card" style={{ maxWidth: 480 }}>
                        {validated ? (
                            <VerifyEmailForm
                                email={accountFormData?.email}
                                customerId={customerId}
                                pageNumber={pageNumber}
                                showNewAuthButton={showNewAuthButton}
                                onEditEmail={() => setValidated(false)}
                            />
                        ) : (
                            <AccountDetailsForm
                                formData={accountFormData}
                                onSubmit={onAccountSubmit}
                                onCreated={() => setValidated(true)}
                            />
                        )}
                    </div>
                </div>
            </Container>
            <PageComponentsList components={bottomRows} />
        </>
    );
}

const THIRTY_MINUTES = 1800000;

const widgets = { iconInput: IconInputWidget };

const accountSchema = {
    type: "object",
    required: ["firstName", "lastName", "email", "repeatEmail", "password", "repeatPassword"],
    properties: {
        firstName: {
            type: "string",
            title: "First Name",
        },
        lastName: {
            type: "string",
            title: "Last Name",
        },
        accountNumber: {
            type: "string",
            title: "Utility Account Number",
        },
        email: {
            type: "string",
            title: "Email Address:",
            format: "email",
        },
        repeatEmail: {
            type: "string",
            title: "Re-Enter Email Address",
            format: "email",
        },
        password: {
            type: "string",
            title: "Password",
        },
        repeatPassword: {
            type: "string",
            title: "Re-Enter Password",
        },
    },
};

const transformErrors = (errors: any[]) =>
    errors.map((e) => {
        if (e.name === "required") {
            return { ...e, message: "Please fill out this field." };
        }
        if (e.name === "format" && e.params?.format === "email") {
            return { ...e, message: "Enter a valid email address." };
        }
        return e;
    });

type AccountDetailsFormProps = {
    formData: any;
    onSubmit: (formData: any, pageNumber: string | null) => void;
    onCreated: () => void;
};

const AccountDetailsForm = ({ formData: accountFormData, onSubmit, onCreated }: AccountDetailsFormProps) => {
    const location = useLocation<{ referrer: string }>();
    const {
        programNumber,
        requirements: { requireAccountNumber },
    } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorSummary, setErrorSummary] = useState<any>();
    const [passwordRequirements, isLoading] = usePasswordRequirements();
    const passwordHelpText = !isLoading && passwordRequirements ? passwordRequirements : undefined;

    const accountUiSchema = {
        firstName: {
            "ui:placeholder": "Enter First Name",
            "ui:options": { autoComplete: "given-name" },
        },
        lastName: {
            "ui:placeholder": "Enter Last Name",
            "ui:options": { autoComplete: "family-name" },
        },
        accountNumber: {
            "ui:placeholder": "Enter Account Number",
            "ui:options": { autoComplete: "off" },
            "ui:widget": requireAccountNumber ? "text" : "hidden",
            classNames: requireAccountNumber ? "pb-4 border-bottom" : undefined,
        },
        email: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "envelope", inputType: "email", autoComplete: "email" },
            "ui:placeholder": "email@example.com",
        },
        repeatEmail: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "envelope", inputType: "email", autoComplete: "email" },
            "ui:placeholder": "email@example.com",
        },
        password: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "lock", inputType: "password", helpIcon: true, autoComplete: "new-password" },
            "ui:help": passwordHelpText,
            "ui:placeholder": "Enter your password",
        },
        repeatPassword: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "lock", inputType: "password", autoComplete: "new-password" },
            "ui:placeholder": "Re-Enter your password",
        },
    };

    function validation(formData: any, errors: any) {
        if (formData.repeatPassword && formData.password !== formData.repeatPassword) {
            errors.repeatPassword.addError("Passwords don't match");
        }
        if (formData.repeatEmail && formData.email !== formData.repeatEmail) {
            errors.repeatEmail.addError("Email addresses don't match");
        }
        return errors;
    }

    const onSubmitAccount = useCallback(
        async ({ formData }: any) => {
            const urlParams = new URLSearchParams(`?${location?.state?.referrer?.split("?")[1]}`);
            const pageNumber = urlParams.get("pageNumber");
            onSubmit(formData, pageNumber);
            setErrorSummary(undefined);
            setIsSubmitting(true);

            createAccount({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password,
                accountNumber: formData.accountNumber,
                programNumber: programNumber,
                pageNumber,
            })
                .then(() => {
                    saveUser({
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email,
                    });
                    onCreated();
                })
                .catch((error) => {
                    setErrorSummary(error);
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [location?.state?.referrer, programNumber, onSubmit, onCreated]
    );

    const emailAlreadyExists = errorSummary?.toString() === "Error: Customer already exists";
    const extraErrors = emailAlreadyExists
        ? {
              email: {
                  __errors: [
                      `Email already in use. Do you need to [Reset Password](${PageLink.ForgotPassword}?email=${encodeURIComponent(
                          accountFormData?.email ?? ""
                      )})?`,
                  ],
              },
          }
        : undefined;

    return (
        <>
            <div className="d-flex">
                <Link
                    to={PageLink.SignIn}
                    className="d-inline-flex align-items-center gap-2 rounded text-decoration-none text-portal-primary fs-6 py-1 px-2 ms-n2"
                >
                    <Icon icon={["fal", "arrow-left"]} />
                    Back to Sign In
                </Link>
            </div>
            <Container className="p-0 d-flex flex-column align-items-center">
                <Icon icon={["far", "user-plus"]} className="fs-1 text-secondary" />
                <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                    Create Account
                </Heading>
                <p className="text-secondary mt-1 text-center">
                    Please provide your details below to create your account and begin using our services.
                </p>
            </Container>
            {errorSummary && !emailAlreadyExists && <ErrorSummary errorSummary={errorSummary} />}
            <JsonForm
                schema={accountSchema}
                uiSchema={accountUiSchema}
                widgets={widgets}
                extraErrors={extraErrors}
                formData={accountFormData}
                onSubmit={onSubmitAccount}
                validate={validation}
                isSubmitting={isSubmitting}
                formName="Create Account"
                buttonClassName="mt-3"
                transformErrors={transformErrors}
            >
                <SubmitButton type="submit" variant="primary" className="w-100" isSubmitting={isSubmitting} spinnerText="Loading...">
                    Submit
                </SubmitButton>
            </JsonForm>
        </>
    );
};

const authenticationSchema = {
    type: "object",
    required: ["authCode"],
    properties: {
        authCode: {
            type: "string",
            title: "Authorization Code",
        },
    },
};

const authenticationUiSchema = {
    authCode: {
        "ui:placeholder": "Enter the Authorization Code",
    },
};

type VerifyEmailFormProps = {
    email?: string;
    customerId?: string;
    pageNumber: string | null;
    showNewAuthButton: boolean;
    onEditEmail: () => void;
};

const VerifyEmailForm = ({ email, customerId, pageNumber, showNewAuthButton, onEditEmail }: VerifyEmailFormProps) => {
    const history = useHistory();
    const location = useLocation<{ referrer: string }>();
    const { programNumber } = useContext(AppContext);
    const customerNumber = getCustomerNumber();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorSummary, setErrorSummary] = useState<any>();
    const [disableNewAuthButton, setDisableNewAuthButton] = useState<boolean>(false);
    const [showGotNewAuthCodeMessage, setShowGotNewAuthCodeMessage] = useState<boolean>(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setDisableNewAuthButton(false);
        }, THIRTY_MINUTES);

        return () => clearInterval(interval);
    }, [disableNewAuthButton]);

    const onSubmitAuthCode = useCallback(
        async ({ formData }: { formData: any }) => {
            setErrorSummary(undefined);
            setIsSubmitting(true);

            verifyAuthCode({
                id: customerNumber ?? customerId,
                authCode: formData.authCode,
            })
                .then(() => {
                    let redirectToApp: string | null = location?.state?.referrer;

                    if (redirectToApp?.startsWith(PageLink.CustomerAuthCode)) {
                        // If the link in the create account email was opened, need to build the redirect url using the pagenumber query param
                        const urlParams = new URLSearchParams(`?${location?.state?.referrer?.split("?")[1]}`);
                        const pageNumber = urlParams.get("pagenumber");
                        redirectToApp = pageNumber ? `${PageLink.Application}?pageNumber=${pageNumber}` : null;
                    }

                    sendAnalyticsEvent(AnalyticsEventType.ACCOUNT_CREATED, { isRedirectToApp: Boolean(redirectToApp) });

                    if (redirectToApp) {
                        history.push(redirectToApp);
                    } else {
                        history.push(PageLink.CustomerHome);
                    }
                })
                .catch((error) => {
                    setErrorSummary(error);
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [customerId, customerNumber, history, location?.state?.referrer]
    );

    const newAuthCode = useCallback(() => {
        setDisableNewAuthButton(true);
        userNewAuthCode({
            programNumber,
            pageNumber,
        })
            .then(() => {
                setShowGotNewAuthCodeMessage(true);
            })
            .catch((error) => {
                setErrorSummary(error);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [programNumber, pageNumber]);

    return (
        <>
            {email && (
                <div className="d-flex justify-content-center">
                    <EmailButton email={email} onClick={onEditEmail} className="mb-4" />
                </div>
            )}
            <div className="d-flex flex-column align-items-center gap-2">
                <Icon icon={["far", "envelope-open-text"]} className="fs-1 text-secondary" />
                <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                    Check your email
                </Heading>
                <p className="text-secondary mt-1 text-center">
                    We've sent a code to confirm your email.
                    <br />
                    It should arrive soon.
                </p>
            </div>
            {errorSummary && <ErrorSummary errorSummary={errorSummary} />}
            <Alert
                dismissible
                onClose={() => setShowGotNewAuthCodeMessage(false)}
                show={showGotNewAuthCodeMessage}
                variant="success"
                className="mb-0"
            >
                <p className="mb-0">A new authorization code has been sent to your email.</p>
            </Alert>
            <JsonForm
                schema={authenticationSchema}
                uiSchema={authenticationUiSchema}
                onSubmit={onSubmitAuthCode}
                isSubmitting={isSubmitting}
                formName="Authorization Code"
                buttonClassName="mt-3"
            >
                <SubmitButton type="submit" variant="primary" className="w-100" isSubmitting={isSubmitting} spinnerText="Loading...">
                    Submit
                </SubmitButton>
            </JsonForm>
            {showNewAuthButton && (
                <Button onClick={newAuthCode} variant="link" className="p-0 align-self-center" disabled={disableNewAuthButton}>
                    Get New Authorization Code
                </Button>
            )}
        </>
    );
};
